import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Role types mirror packages/types/src/schemas/contact.schema.ts:86 (roleTypes).
const ROLE_LABELS: Record<string, string> = {
  customer: "Customer",
  team_member: "Team Member",
  trade_partner: "Trade Partner",
};

export function roleLabel(role: string | null | undefined): string {
  if (!role) return "—";
  return ROLE_LABELS[role] ?? role.replace(/_/g, " ");
}

// Role badge — neutral outline. We intentionally do NOT use <StatusBadge> here:
// it tints `customer` green, which reads as a lifecycle status rather than a role.
export function RoleBadge({
  role,
  className,
}: {
  role: string | null | undefined;
  className?: string;
}) {
  if (!role) return null;
  return (
    <Badge
      variant="outline"
      className={cn(
        "rounded-sm typo-label border-[color:var(--color-border)] text-[color:var(--color-text-md)] bg-transparent",
        className,
      )}
    >
      {roleLabel(role)}
    </Badge>
  );
}

// Resolve a role to its clickable host-entity detail path, keyed by contact_id.
// Only `customer` has a registered detail route (`/customers/:id`). `team_member`
// has no detail route yet (App.tsx registers only the `/team-members` list) and
// `trade_partner` has no host page at all — both return null so we never link to
// a route that 404s. Add team_member here once its detail route lands.
export function hostPathForRole(
  roleType: string | null | undefined,
  contactId: string | null | undefined,
): string | null {
  if (!contactId) return null;
  if (roleType === "customer") return `/customers/${contactId}`;
  return null;
}

// Clickable link to a role's host entity. Stops propagation so it doesn't also
// trigger the surrounding row's onClick. Renders a plain dash when no host page
// exists (e.g. trade_partner).
export function HostLink({
  roleType,
  contactId,
}: {
  roleType: string | null | undefined;
  contactId: string | null | undefined;
}) {
  const path = hostPathForRole(roleType, contactId);
  if (!path) {
    return <span className="text-[color:var(--color-text-lo)]">—</span>;
  }
  return (
    <Link
      to={path}
      onClick={(e) => e.stopPropagation()}
      className="text-[color:var(--color-accent)] hover:underline"
      aria-label={`View ${roleLabel(roleType)} record`}
    >
      View →
    </Link>
  );
}
